import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import allUserSessionsService, { type AllUserSessionsResponse } from "../services/all-user-sessions-service";

/**
 * Query keys for all user sessions
 */
export const allUserSessionsKeys = {
	all: ["all-user-sessions"] as const,
	list: (params?: { status?: string; user_id?: string }) => [...allUserSessionsKeys.all, "list", params] as const,
};

/**
 * Query options for getting all user sessions
 */
export const allUserSessionsQueryOptions = (params?: {
	status?: "active" | "logged_out" | "all";
	user_id?: string;
}) =>
	queryOptions({
		queryKey: allUserSessionsKeys.list(params),
		queryFn: () => allUserSessionsService.getAllSessions(params),
		staleTime: 1000 * 10, // 10 seconds
		refetchInterval: 1000 * 30, // Refetch every 30 seconds
	});

/**
 * Mutation for terminating a session
 */
export const useTerminateSession = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (sessionId: number) => allUserSessionsService.terminateSession(sessionId),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: allUserSessionsKeys.all });
			toast.success(data.message || "Session terminated successfully");
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message || "Failed to terminate session");
		},
	});
};

/**
 * Mutation for terminating all sessions for a user
 */
export const useTerminateUserSessions = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (userId: string) => allUserSessionsService.terminateUserSessions(userId),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: allUserSessionsKeys.all });
			toast.success(data.message || "User sessions terminated successfully");
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message || "Failed to terminate user sessions");
		},
	});
};

/**
 * Mutation for terminating all active sessions
 */
export const useTerminateAllSessions = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => allUserSessionsService.terminateAllSessions(),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: allUserSessionsKeys.all });
			toast.success(data.message || "All sessions terminated successfully");
		},
		onError: (error: any) => {
			toast.error(error?.response?.data?.message || "Failed to terminate all sessions");
		},
	});
};
